import { useReactiveVar } from '@apollo/client'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
  BucketMediaContentOwner,
  BucketMediaFilter,
  BucketMediaSorter,
  BucketMediaSourceFilter,
  BucketMediaTypeFilter,
  BucketSortingField,
  BucketSortingOrder,
  BucketType
} from '../../../../graphql/generated'
import { modalVar } from '../../../../providers/apollo/cache'
import useBucketGallerySettings from '../../hooks/useBucketGallerySettings'
import useBucket from '../../hooks/useBucket'

// Helper function to map settings to sortValue string
const mapSettingsToSortValue = (sortBy: BucketMediaSorter | null | undefined): string => {
  if (!sortBy) return 'dateTakenDesc' // Default if no sort settings exist
  const { field, order } = sortBy
  if (field === BucketSortingField.DateTaken) {
    return order === BucketSortingOrder.Desc ? 'dateTakenDesc' : 'dateTakenAsc'
  }
  if (field === BucketSortingField.Date) {
    return order === BucketSortingOrder.Desc ? 'dateUploadedDesc' : 'dateUploadedAsc'
  }
  if (field === BucketSortingField.Title) {
    // return 'titleAsc' // Assuming Title is always Asc for now, adjust if needed
    return order === BucketSortingOrder.Desc ? 'titleDesc' : 'titleAsc'
  }
  return 'dateTakenDesc' // Default fallback
}

// Helper function to map sortValue string to settings
const mapSortValueToSettings = (sortValue: string): BucketMediaSorter => {
  switch (sortValue) {
    case 'dateTakenAsc':
      return { field: BucketSortingField.DateTaken, order: BucketSortingOrder.Asc }
    case 'dateUploadedDesc':
      return { field: BucketSortingField.Date, order: BucketSortingOrder.Desc }
    case 'dateUploadedAsc':
      return { field: BucketSortingField.Date, order: BucketSortingOrder.Asc }
    case 'titleAsc':
      return { field: BucketSortingField.Title, order: BucketSortingOrder.Asc }
    case 'titleDesc':
      return { field: BucketSortingField.Title, order: BucketSortingOrder.Desc }
    case 'dateTakenDesc':
    default:
      return { field: BucketSortingField.DateTaken, order: BucketSortingOrder.Desc }
  }
}

export default function FilterAndSort() {
  const modal = useReactiveVar(modalVar)
  const { settings, updateSettings } = useBucketGallerySettings()
  const { bucket } = useBucket()
  const bucketType = bucket?.bucketType

  // Initial state from settings
  const [sortValue, setSortValue] = useState(() => mapSettingsToSortValue(settings.sortBy))
  const [filterByValue, setFilterByValue] = useState(() => {
    if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Image) return 'photos'
    if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Video) return 'videos'
    return 'all'
  })
  const [isFavorite, setIsFavorite] = useState(!!settings.filterBy?.isFavorite)
  const [isSharedByOthers, setIsSharedByOthers] = useState(settings.filterBy?.owner === BucketMediaContentOwner.Others)
  const [isMobile, setIsMobile] = useState(settings.filterBy?.source === BucketMediaSourceFilter.Mobile)

  useEffect(() => {
    // Update local state if gallery settings change externally
    setSortValue(mapSettingsToSortValue(settings.sortBy))
    setFilterByValue(() => {
      if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Image) return 'photos'
      if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Video) return 'videos'
      return 'all'
    })
    setIsFavorite(!!settings.filterBy?.isFavorite)
    setIsSharedByOthers(settings.filterBy?.owner === BucketMediaContentOwner.Others)
    setIsMobile(settings.filterBy?.source === BucketMediaSourceFilter.Mobile)
  }, [settings.sortBy, settings.filterBy])

  const handleClose = () => {
    modalVar(null)
    // Reset local state to match global settings if the modal is cancelled
    setSortValue(mapSettingsToSortValue(settings.sortBy))
    setFilterByValue(() => {
      if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Image) return 'photos'
      if (settings.filterBy?.mediaType === BucketMediaTypeFilter.Video) return 'videos'
      return 'all'
    })
    setIsFavorite(!!settings.filterBy?.isFavorite)
    setIsSharedByOthers(settings.filterBy?.owner === BucketMediaContentOwner.Others)
    setIsMobile(settings.filterBy?.source === BucketMediaSourceFilter.Mobile)
  }

  const handleClick = () => {
    const newSortSettings = mapSortValueToSettings(sortValue)
    const newFilterSettings: BucketMediaFilter = {}

    // 'all' means mediaType is null/undefined (no filter)
    newFilterSettings.mediaType = null
    if (filterByValue === 'photos') {
      newFilterSettings.mediaType = BucketMediaTypeFilter.Image
    } else if (filterByValue === 'videos') {
      newFilterSettings.mediaType = BucketMediaTypeFilter.Video
    }

    newFilterSettings.isFavorite = isFavorite

    if (bucketType !== BucketType.Mybucket) {
      newFilterSettings.owner = isSharedByOthers ? BucketMediaContentOwner.Others : BucketMediaContentOwner.Me
    }

    newFilterSettings.source = isMobile ? BucketMediaSourceFilter.Mobile : null

    // Clean up empty filter object if no filters are applied
    const filterSettingsToSend = Object.keys(newFilterSettings).length > 0 ? newFilterSettings : null

    updateSettings({ sortBy: newSortSettings, filterBy: filterSettingsToSend }, { suppressAlerts: true })
    handleClose()
  }

  return (
    <div>
      <Dialog open={modal === 'filterAndSort'} onClose={handleClose} aria-labelledby="filter-sort-dialog-title" maxWidth="sm" fullWidth>
        <DialogTitle id="filter-sort-dialog-title">Filter and Sort</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Sort Options */}
            <FormControl component="fieldset">
              {/* Removed FormLabel as title is implicit */}
              <RadioGroup aria-label="sort-by" name="sort-by-group" value={sortValue} onChange={(e) => setSortValue(e.target.value)}>
                <FormControlLabel
                  value="dateTakenDesc"
                  control={<Radio />}
                  label="Date Taken (Newest to Oldest)"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="dateTakenAsc"
                  control={<Radio />}
                  label="Date Taken (Oldest to Newest)"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="dateUploadedDesc"
                  control={<Radio />}
                  label="Date Uploaded (Newest to Oldest)"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="dateUploadedAsc"
                  control={<Radio />}
                  label="Date Uploaded (Oldest to Newest)"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="titleAsc"
                  control={<Radio />}
                  label="Title (A to Z)"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="titleDesc"
                  control={<Radio />}
                  label="Title (Z to A)"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
              </RadioGroup>
            </FormControl>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                Filter By
              </FormLabel>
              <RadioGroup aria-label="filter-by" name="filter-by-group" value={filterByValue} onChange={(e) => setFilterByValue(e.target.value)}>
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label="All"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="photos"
                  control={<Radio />}
                  label="Photos"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                <FormControlLabel
                  value="videos"
                  control={<Radio />}
                  label="Videos"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
              </RadioGroup>
            </FormControl>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                You Can Also Filter By These Cool Options
              </FormLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  control={<Checkbox checked={isFavorite} onChange={(e) => setIsFavorite(e.target.checked)} name="favorites" />}
                  label="Favorites"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
                {bucketType !== BucketType.Mybucket && (
                  <FormControlLabel
                    control={<Checkbox checked={isSharedByOthers} onChange={(e) => setIsSharedByOthers(e.target.checked)} name="sharedByOthers" />}
                    label="Shared by Others"
                    sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                  />
                )}
                <FormControlLabel
                  control={<Checkbox checked={isMobile} onChange={(e) => setIsMobile(e.target.checked)} name="mobileUploads" />}
                  label="Mobile Uploads"
                  sx={{ my: '-4px', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: 'text.secondary' } }}
                />
              </Box>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 1.2, px: 2 }}>
          <Button color="inherit" onClick={handleClose} variant="outlined" sx={{ borderRadius: '50px', padding: '8px 60px' }}>
            Cancel
          </Button>
          <Button onClick={handleClick} variant="contained" sx={{ borderRadius: '50px', padding: '8px 60px' }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
